import {
  useAcquireControlLock,
  useBatches,
  useControlLock,
  useCurrentUser,
  usePermissions,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { useControlDisabled } from '@ctcells/realtime'
import { useEffect, useState } from 'react'

import { ACTIVE_BATCH_LIMIT } from './activeBatchSource.ts'
import { BatchHaltDialog } from './BatchHaltDialog.tsx'
import { batchHaltStore, useBatchHaltIds } from './batchHaltStore.ts'
import { toControlLockView } from './batchStripModel.ts'

/** 기존 활성 작업 캐시를 관찰한다. batch_status가 무효화하며 별도 연결/폴링은 없다. */
export function BatchHaltDialogHost() {
  const query = useBatches({
    limit: ACTIVE_BATCH_LIMIT,
    phase: 'active',
    skip: dataSourceFor('batches') !== 'live',
  })
  const ids = useBatchHaltIds()
  const lock = useControlLock()
  const acquireLock = useAcquireControlLock()
  const [acquireError, setAcquireError] = useState<string | null>(null)
  const me = useCurrentUser()
  const permissions = usePermissions()
  const controlDisabled = useControlDisabled()
  const items = query.data?.items
  useEffect(() => {
    if (items !== undefined && !query.isError) batchHaltStore.observe(items)
  }, [items, query.isError])
  const batches = (items ?? []).filter(
    (batch) =>
      ids.includes(batch.batch_id) && (batch.status === 'paused' || batch.status === 'error'),
  )
  if (batches.length === 0) return null
  const lockView = toControlLockView(lock, me?.name ?? me?.username)
  /*
   * 보유자 표시 이름 — 서버 `GET /control-token` · `control_lock` 사건의 `holder.display_name`(작업 줄 칩과 같은 값).
   * 내가 쥐었는데 그 값이 아직 없으면(획득 직후) 내 계정 표시 이름. 둘 다 없으면 팝업이 「내가 보유」로 떨어진다.
   * ⚠ `lockView.holder` 는 이미 「○○ 보유」로 접힌 문구라 넘기지 않는다 — 팝업이 한 번 더 「보유」를 붙인다
   */
  const lockName = nonBlank(lock.displayName)
  const holderName = lock.held ? (lockName ?? nonBlank(me?.name)) : undefined
  const holder =
    lockView.kind === 'other'
      ? (lockName ?? t('lock.holderUnknown'))
      : 'holder' in lockView
        ? lockView.holder
        : t(
            lockView.kind === 'unknown'
              ? 'lock.unknown'
              : lockView.kind === 'expired'
                ? 'lock.expired'
                : 'lock.none',
          )
  const lockGateReason =
    controlDisabled || query.isError
      ? t('reason.streamStale')
      : acquireLock.isPending
        ? t('reason.actionInFlight')
        : permissions?.includes('control')
          ? undefined
          : t('reason.noPermission', { perm: 'control' })
  return (
    <BatchHaltDialog
      batches={batches}
      holder={holder}
      {...(holderName === undefined ? {} : { holderName })}
      access={{
        permissions,
        controlDisabled: controlDisabled || query.isError,
        lockHeld: lock.held,
      }}
      acquireGate={
        lockGateReason === undefined
          ? { disabled: false }
          : { disabled: true, disabledReason: lockGateReason }
      }
      acquireError={acquireError}
      onAcquireLock={() => {
        if (lockGateReason !== undefined) return
        setAcquireError(null)
        // BatchStripContainer의 onLockAction('acquire')와 같은 명령·본문·콜백 옵션.
        acquireLock.mutate(
          {},
          {
            onError: (error) => setAcquireError(errorMessage(error)),
            onSuccess: () => setAcquireError(null),
          },
        )
      }}
      onClose={batchHaltStore.dismiss}
    />
  )
}

function nonBlank(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed === undefined || trimmed === '' ? undefined : trimmed
}
